// =======================================================
// BACKTESTX DATAFEED (1m BASE + MULTI-YEAR BIN - PRODUCTION)
// ✅ Multi-year: XAU-USD_2023.bin, XAU-USD_2024.bin, XAU-USD_2025.bin
// ✅ Default loads latest year only
// ✅ Scroll back auto-loads previous year (lazy loading)
// ✅ Chunked BIN safe (Android can send base64 chunks)
// ✅ No sort / no giant array copies
// ✅ Respects periodParams.from/to (prevents TradingView freeze)
// ✅ Replay runs on 1m pointer, aggregated to active TF
// =======================================================

// ===============================
// GLOBAL STATE
// ===============================
window.replayMode = false;
window.replayEnd = null;

window.barCache = { "1": [] }; // merged 1m bars across loaded years (sorted by time)
window.replayIndex = -1;
window._lastReplayPrice = null;

let _currentResolution = null;
const lastBarsCache = new Map(); // 🔥 Required by TradingView (same as demo datafeed)
// ===============================
// TF SESSION MEMORY (CRITICAL)
// ===============================
window._lastTfUsed = null;
window._deepHistoryLoaded = false;

window._onRealtimeCallback = null;
window._isReadyForRealtime = false;
window._activeSubscriber = { uid: null, symbol: null, resolution: null };

// 🔥 TF SWITCH RACE GUARD: incremented every time a new TF switch starts
// subscribeBars checks its snapshot of this token and rejects stale calls
window._tfSwitchSeq = 0;

// 🔥 SELF-SUBSCRIBE FALLBACK: TV sometimes calls getBars but never subscribeBars
// after a fast TF switch. We keep the last known callback so getBars can
// self-activate the pump without waiting for TV's delayed subscribeBars.
window._lastOnRealtimeCallback = null;
window._lastSubscriberUID = null;

// 🔥 PRE-SWITCH BACKUP: saved from the old subscription BEFORE a TF switch clears
// _lastOnRealtimeCallback. Used by getBars when TV delays all subscribeBars calls.
window._preSwitchCallback = null;
window._preSwitchUID = null;

let _isPumpBusy = false; // REQUIRED for replay pump safety

// Multi-year support
window.loadedYears = new Set();
window.loadingYear = null;
window.missingYears = new Set(); // 🔥 ADD THIS (missing file memory)

// 🔥 SESSION RESTORE CACHE RESET
// Must be called BEFORE startAutoReplay when restoring a session.
// Wipes stale bars so TV never sees time-order violations.
window.resetBarCacheForRestore = () => {
  window.barCache = { "1": [] };
  window.loadedYears.clear();
  window.loadingYear = null;
  window.replayIndex = -1;
  window.replayEnd = null;
  window._lastReplayPrice = null;
  window._lastEmittedCandleTime = 0;
  window._lastTfUsed = null;
  window._deepHistoryLoaded = false;
  window._onRealtimeCallback = null;
  window._isReadyForRealtime = false;
  window._preSwitchCallback = null;
  window._preSwitchUID = null;
  window._lastOnRealtimeCallback = null;
  window._lastSubscriberUID = null;
  console.log('🧹 Bar cache fully reset for session restore');
};


// EDIT: years you have files for
const AVAILABLE_YEARS = [2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025];

let _pendingResolve = null;
let _tvLoaderStart = 0;
let _loadToken = 0;

// Chunk buffer for base64
let _base64Buffer = "";
// ===============================
// NEWS CACHE (GLOBAL)
// ===============================
window.newsEvents = [];
window._newsLoaded = false;

// Impact color mapping
const NEWS_COLORS = {
  1: "#9E9E9E", // Low
  2: "#FFC107", // Medium (Yellow)
  3: "#FF5252"  // High (Red)
};
// ===============================
// NEWS BIN LOADER (ANDROID + BIN)
// ADD THIS BELOW NEWS CACHE BLOCK
// ===============================
window.loadNewsBINForSymbol = async (symbolName) => {
  try {
    // 🔥 Convert candle symbol (XAUUSD) → news symbol (XAU-USD)
    let baseSymbol = symbolName.replace("_REPLAY", "").toUpperCase();

    if (baseSymbol === "XAUUSD") baseSymbol = "XAU-USD";
    if (baseSymbol === "BTCUSD") baseSymbol = "BTC-USD";
    if (baseSymbol === "ETHUSD") baseSymbol = "ETH-USD";
    // Prevent reloading same news repeatedly
    if (window._newsLoaded && window._newsSymbol === baseSymbol) {
      return window.newsEvents;
    }

    console.log("📰 Loading NEWS BIN for:", baseSymbol);

    let base64 = "";
    if (window.AndroidBridge && window.AndroidBridge.loadNewsBIN) {
      base64 = window.AndroidBridge.loadNewsBIN(baseSymbol);
    } else {
      console.log("🌐 Web fallback: Fetching NEWS from IndexedDB");
      base64 = await new Promise((resolve) => {
        const fileName = `${baseSymbol}_news.bin`;
        const key = `${baseSymbol}/${fileName}`;
        const req = indexedDB.open("BacktestxData", 2);
        req.onsuccess = (e) => {
          const db = e.target.result;
          if (!db.objectStoreNames.contains("files")) {
            resolve("");
            return;
          }
          const tx = db.transaction("files", "readonly");
          const store = tx.objectStore("files");
          const getReq = store.get(key);
          getReq.onsuccess = () => {
            const buffer = getReq.result;
            if (!buffer) { resolve(""); return; }
            const bytes = new Uint8Array(buffer);
            let binary = "";
            for (let i = 0; i < bytes.length; i++) {
              binary += String.fromCharCode(bytes[i]);
            }
            resolve(btoa(binary));
          };
          getReq.onerror = () => resolve("");
        };
        req.onerror = () => resolve("");
      });
    }

    if (!base64 || base64.length === 0) {
      console.warn("No NEWS BIN found for:", baseSymbol);
      window.newsEvents = [];
      window._newsLoaded = true;
      window._newsSymbol = baseSymbol;
      return [];
    }

    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    // NEWS STRUCT (recommended)
    // <q B 80s> = time(8) + impact(1) + title(80) = 89 bytes
    const NEWS_STRUCT_SIZE = 89;
    const view = new DataView(bytes.buffer);

    const parsedNews = [];

    for (let offset = 0; offset + NEWS_STRUCT_SIZE <= bytes.length; offset += NEWS_STRUCT_SIZE) {
      let t = Number(view.getBigInt64(offset, true));
      if (t < 1_000_000_000_000) t *= 1000;

      const impact = view.getUint8(offset + 8);

      const titleBytes = bytes.slice(offset + 9, offset + 89);
      const title = new TextDecoder()
        .decode(titleBytes)
        .replace(/\0/g, "")
        .trim();

      parsedNews.push({
        time: t,
        impact: impact,
        title: title || "News Event"
      });
    }

    window.newsEvents = parsedNews;
    if (window.tvWidgetRef?.current) {
      try {
        window.tvWidgetRef.current.activeChart().refreshMarks();
        console.log("📰 Marks refreshed after news load");
      } catch (e) { }
    }
    window._newsLoaded = true;
    window._newsSymbol = baseSymbol;

    console.log("✅ NEWS loaded:", parsedNews.length);
    return parsedNews;

  } catch (err) {
    console.error("❌ NEWS BIN parse failed", err);
    window.newsEvents = [];
    return [];
  }
};

// TF cache: cache FULL aggregation per resolution for current loaded dataset
//
// ===============================
// REALTIME EMIT BATCHER (prevents broken candles on high speed)
// ===============================
const emitRealtime = (bar) => {
  if (window._onRealtimeCallback) {
    window._onRealtimeCallback({ ...bar });
  }
};


// ===============================
// TV LOADER
// ===============================
const showTVLoader = () => {
  _tvLoaderStart = Date.now();
  window.tvWidgetRef?.current?.showLoadingScreen?.();
};

const hideTVLoader = () => {
  const elapsed = Date.now() - _tvLoaderStart;
  const wait = Math.max(0, 3000 - elapsed);
  setTimeout(() => {
    window.tvWidgetRef?.current?.hideLoadingScreen?.();
  }, wait);
};

// ===============================
// FILE NAME (YEAR BIN)
// ===============================
const getYearFileName = (symbol, year) => {
  const clean = symbol.replace("_REPLAY", "");
  return `${clean}_${year}.bin`;
};

// ===============================
// TIMEFRAME HELPERS
// ===============================
const getResolutionMs = (resolution) => {
  switch (resolution) {
    case "1": return 60000;
    case "5": return 300000;
    case "15": return 900000;
    case "30": return 1800000;
    case "60": return 3600000;
    case "240": return 14400000;
    case "D":
    case "1D": return 86400000;
    default: return parseInt(resolution) * 60000;
  }
};

// binary search index <= time
const findIndexByTime = (t) => {
  const bars = window.barCache["1"];
  if (!bars.length) return -1;

  let lo = 0, hi = bars.length - 1, ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (bars[mid].time <= t) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
};

// binary search first index >= time
const lowerBoundByTime = (t) => {
  const bars = window.barCache["1"];
  let lo = 0, hi = bars.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (bars[mid].time < t) lo = mid + 1;
    else hi = mid;
  }
  return lo;
};

// ===============================
// BIN -> 1m BARS (CHUNK SAFE)
// ===============================
window.onTimeframeBIN = (dataString, isChunk = false) => {
  try {
    if (isChunk) {
      _base64Buffer += dataString;
      return;
    }

    const finalBase64 =
      _base64Buffer.length > 0 ? _base64Buffer + dataString : dataString;
    _base64Buffer = "";

    console.log("📥 BIN decode start, base64 length:", finalBase64.length);

    const binary = atob(finalBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    const BAR_SIZE = 28;
    const totalBars = Math.floor(bytes.length / BAR_SIZE);

    const cache = window.barCache["1"];

    let added = 0;
    let skippedDuplicates = 0;
    let olderBarsInserted = 0;

    for (let i = 0; i + BAR_SIZE <= bytes.length; i += BAR_SIZE) {
      const view = new DataView(bytes.buffer, i, BAR_SIZE);

      let t = Number(view.getBigInt64(0, true));
      if (t < 1_000_000_000_000) t *= 1000;

      const bar = {
        time: t,
        open: view.getFloat32(8, true),
        high: view.getFloat32(12, true),
        low: view.getFloat32(16, true),
        close: view.getFloat32(20, true),
        volume: view.getFloat32(24, true),
      };

      // 🔥 ONLY skip exact duplicate timestamps (NOT older years!)
      if (cache.length > 0) {
        const last = cache[cache.length - 1];
        if (bar.time === last.time) {
          skippedDuplicates++;
          continue;
        }
      }

      // 🔥 If loading older year (time < first candle) → insert at START
      if (cache.length > 0 && bar.time < cache[0].time) {
        cache.unshift(bar);
        olderBarsInserted++;
      } else {
        // Normal append (latest year or future data)
        cache.push(bar);
      }

      added++;
    }

    console.log("📊 BIN PARSE REPORT:");
    console.log("Total bars in file =", totalBars);
    console.log("Added bars =", added);
    console.log("Older bars inserted =", olderBarsInserted);
    console.log("Skipped duplicates =", skippedDuplicates);
    console.log("Merged cache size =", cache.length);
    console.log(
      "Cache time range:",
      new Date(cache[0]?.time),
      "→",
      new Date(cache[cache.length - 1]?.time)
    );

    // 🔥 CRITICAL: ensure dataset stays time-sorted after unshift
    // 🔥 OPTIMIZED: Only sort if older bars were inserted
    if (olderBarsInserted > 0) {
      cache.sort((a, b) => a.time - b.time);
    }

    if (_pendingResolve) {
      _pendingResolve();
      _pendingResolve = null;
    }
  } catch (err) {
    console.error("❌ [DATAFEED] BIN parse failed", err);
    _base64Buffer = "";
    if (_pendingResolve) {
      _pendingResolve();
      _pendingResolve = null;
    }
  }
};
// ===============================
// AGGREGATION
// ===============================
const buildCandle = (bars, time) => {
  const open = bars[0].open;
  const close = bars[bars.length - 1].close;

  let high = bars[0].high;
  let low = bars[0].low;
  let vol = 0;

  for (let i = 0; i < bars.length; i++) {
    const b = bars[i];
    if (b.high > high) high = b.high;
    if (b.low < low) low = b.low;
    vol += Number(b.volume || 0);
  }

  return { time, open, high, low, close, volume: vol };
};

// FULL aggregation for current loaded dataset (cached per resolution)
// ===============================
// LIGHTWEIGHT RANGE AGGREGATION (FIX HEAVY 1D)
// Only aggregates visible range instead of full dataset
// ===============================
const aggregateRangeFrom1m = (resolution, fromMs, toMs, uptoIndex = null, countBack = 300) => {
  const oneMinBars = window.barCache["1"];
  if (!oneMinBars.length) return [];

  const tfMs = getResolutionMs(resolution);

  // Add buffer so TradingView never loops infinitely
  const buffer = Math.max(200, countBack + 50);
  const safeFrom = Math.max(0, fromMs - (buffer * tfMs));

  // Decide end index
  let endIdx = (uptoIndex !== null)
    ? Math.min(uptoIndex, oneMinBars.length - 1)
    : oneMinBars.length - 1;

  // Find start index using binary search (FAST)
  let startIdx = lowerBoundByTime(safeFrom);
  if (startIdx < 0) startIdx = 0;
  if (startIdx > endIdx) startIdx = Math.max(0, endIdx - 1000);

  const result = [];

  let groupTime = null;
  let open = 0, high = 0, low = 0, close = 0, vol = 0;

  for (let i = startIdx; i <= endIdx; i++) {
    const b = oneMinBars[i];
    if (!b) continue;

    const bucket = Math.floor(b.time / tfMs) * tfMs;

    // Stop if beyond requested time (important for 1D scroll)
    if (bucket > toMs + tfMs) break;

    if (groupTime === null) {
      groupTime = bucket;
      open = b.open;
      high = b.high;
      low = b.low;
      close = b.close;
      vol = b.volume || 0;
      continue;
    }

    if (bucket !== groupTime) {
      result.push({ time: groupTime, open, high, low, close, volume: vol });

      groupTime = bucket;
      open = b.open;
      high = b.high;
      low = b.low;
      close = b.close;
      vol = b.volume || 0;
    } else {
      if (b.high > high) high = b.high;
      if (b.low < low) low = b.low;
      close = b.close;
      vol += b.volume || 0;
    }
  }

  if (groupTime !== null) {
    result.push({ time: groupTime, open, high, low, close, volume: vol });
  }

  // Respect TradingView countBack (CRITICAL)
  let last = result.length - 1;
  while (last >= 0 && result[last].time > toMs) last--;
  if (last < 0) last = result.length - 1;

  const first = Math.max(0, last - countBack);
  return result.slice(first, last + 1);
};


// Replay aggregation only up to replayIndex (NOT cached by index)
const aggregateReplayFrom1m = (resolution, uptoIndex) => {
  const oneMinBars = window.barCache["1"];
  if (!oneMinBars.length || uptoIndex < 0) return [];

  const tfMs = getResolutionMs(resolution);
  const result = [];

  let group = [];
  let groupTime = null;

  const lastIdx = Math.min(uptoIndex, oneMinBars.length - 1);

  for (let i = 0; i <= lastIdx; i++) {
    const b = oneMinBars[i];
    const bucket = Math.floor(b.time / tfMs) * tfMs;

    if (groupTime === null) groupTime = bucket;

    if (bucket !== groupTime) {
      result.push(buildCandle(group, groupTime));
      group = [];
      groupTime = bucket;
    }
    group.push(b);
  }

  if (group.length) result.push(buildCandle(group, groupTime));
  return result;
};
// 🔥 ULTRA FAST: Build ONLY current TF candle (NO full history rebuild)
const buildCurrentReplayCandle = (resolution, uptoIndex) => {
  const oneMinBars = window.barCache["1"];
  if (!oneMinBars.length || uptoIndex < 0) return null;

  const tfMs = getResolutionMs(resolution);
  const endBar = oneMinBars[uptoIndex];
  if (!endBar) return null;

  const bucketTime = Math.floor(endBar.time / tfMs) * tfMs;

  let firstIndex = uptoIndex;
  let high = endBar.high;
  let low = endBar.low;
  let volume = endBar.volume || 0;

  // 🔥 Find FIRST bar of this TF bucket (TRUE OPEN)
  for (let i = uptoIndex - 1; i >= 0; i--) {
    const b = oneMinBars[i];
    const bBucket = Math.floor(b.time / tfMs) * tfMs;

    if (bBucket !== bucketTime) break;

    firstIndex = i;

    if (b.high > high) high = b.high;
    if (b.low < low) low = b.low;
    volume += b.volume || 0;
  }

  const firstBar = oneMinBars[firstIndex];

  return {
    time: bucketTime,
    open: firstBar.open,   // ✅ TRUE OPEN (FIXED)
    high: high,
    low: low,
    close: endBar.close,
    volume: volume,
  };
};

// ===============================
// CHECK WHICH YEARS ARE DOWNLOADED
// ===============================
const getDownloadedYearsForSymbol = async (symbolName) => {
  // On Android: just check which files exist using the bridge
  if (window.AndroidBridge && window.AndroidBridge.getAvailableYears) {
    try {
      const raw = window.AndroidBridge.getAvailableYears(symbolName);
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed.map(Number);
    } catch { /* fall through */ }
  }

  // On Android without getAvailableYears: probe each year file synchronously
  if (window.AndroidBridge && window.AndroidBridge.loadTimeframeBIN) {
    const available = [];
    for (const year of AVAILABLE_YEARS) {
      const fileName = `${symbolName}_${year}.bin`;
      try {
        const result = window.AndroidBridge.loadTimeframeBIN(fileName);
        // if bridge returns non-empty it exists (some bridges return "" for missing)
        if (result && result.length > 0) available.push(year);
      } catch { /* missing */ }
    }
    if (available.length > 0) return available;
    // fallback: if probe didn't work, trust AVAILABLE_YEARS
    return [...AVAILABLE_YEARS];
  }

  // On Web: check IndexedDB for keys matching this symbol + year
  return new Promise((resolve) => {
    const req = indexedDB.open("BacktestxData", 2);
    req.onerror = () => resolve([...AVAILABLE_YEARS]); // fallback
    req.onsuccess = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains("files")) {
        resolve([...AVAILABLE_YEARS]);
        return;
      }
      const tx = db.transaction("files", "readonly");
      const store = tx.objectStore("files");
      const keysReq = store.getAllKeys();
      keysReq.onsuccess = () => {
        const keys = keysReq.result; // e.g. ["XAU-USD/XAU-USD_2024.bin", ...]
        const found = [];
        for (const year of AVAILABLE_YEARS) {
          const fileName = `${symbolName}_${year}.bin`;
          const key = `${symbolName}/${fileName}`;
          if (keys.includes(key)) found.push(year);
        }
        console.log("📦 Downloaded years found in IndexedDB:", found);
        resolve(found.length > 0 ? found : [...AVAILABLE_YEARS]);
      };
      keysReq.onerror = () => resolve([...AVAILABLE_YEARS]);
    };
  });
};

// ===============================
// WEB BIN LOADER (IndexedDB)
// ===============================

const loadBINFromIndexedDB = async (fileName) => {

  try {

    const symbol = fileName.split("_")[0];
    const key = `${symbol}/${fileName}`;

    const req = indexedDB.open("BacktestxData", 2);

    req.onsuccess = (e) => {

      const db = e.target.result;

      const tx = db.transaction("files", "readonly");
      const store = tx.objectStore("files");

      const getReq = store.get(key);

      getReq.onsuccess = () => {

        const buffer = getReq.result;

        if (!buffer) {
          console.error("❌ BIN not found:", key);
          if (typeof _pendingResolve === 'function') _pendingResolve();
          return;
        }

        const bytes = new Uint8Array(buffer);

        let binary = "";
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }

        const base64 = btoa(binary);

        window.onTimeframeBIN(base64, false);
      };

    };

  } catch (err) {
    console.error("BIN load failed", err);
    if (typeof _pendingResolve === 'function') _pendingResolve();
  }

};

// ===============================
// YEAR LOADING
// ===============================
const loadYearIfNeeded = async (symbolInfo, year) => {
  // 🔥 CRITICAL: Never retry missing years (instant stop)
  if (window.missingYears.has(year)) {
    console.log(`⛔ Skipping missing year (cached): ${year}`);
    return;
  }

  if (window.loadedYears.has(year) || window.loadingYear === year) return;

  window.loadingYear = year;
  const fileName = getYearFileName(symbolInfo.name, year);

  console.log(`📦 Loading year ${year}: ${fileName}`);

  await new Promise((resolve) => {
    const myToken = ++_loadToken;
    let resolved = false;

    const safeResolve = () => {
      if (resolved) return;
      resolved = true;
      window.loadingYear = null;
      resolve();
    };

    _pendingResolve = () => {
      if (myToken !== _loadToken) return;

      window.loadedYears.add(year);
      console.log(`✅ Year loaded: ${year}. loadedYears=`, Array.from(window.loadedYears));
      safeResolve();
    };

    // 🔥 Try loading from Android
    // ===============================
    // WEB BIN LOADER
    // ===============================

    if (window.AndroidBridge && window.AndroidBridge.loadTimeframeBIN) {
      // Android app
      window.AndroidBridge.loadTimeframeBIN(fileName);
    } else {
      // Web version
      loadBINFromIndexedDB(fileName);
    }
    // 🚨 FAST TIMEOUT (NOT 15s!)
    setTimeout(() => {
      if (window.loadingYear === year) {
        console.warn(`❌ Missing BIN file detected: ${year}`);

        // 🔥 MARK AS MISSING (KEY FIX)
        window.missingYears.add(year);

        console.log("📁 MissingYears cache:", Array.from(window.missingYears));

        safeResolve(); // resolve instantly after short wait
      }
    }, 2500); // ⭐ 2.5s instead of 15s (massive UX fix)
  });
};

const getYearRangeIndexes = (year) => {
  const bars = window.barCache["1"];
  if (!bars.length) return null;

  const startTime = Date.UTC(year, 0, 1);
  const endTime = Date.UTC(year + 1, 0, 1) - 1;

  let startIdx = -1;
  let endIdx = -1;

  for (let i = 0; i < bars.length; i++) {
    const t = bars[i].time;
    if (t >= startTime && t <= endTime) {
      if (startIdx === -1) startIdx = i;
      endIdx = i;
    }
  }

  if (startIdx === -1 || endIdx === -1) return null;
  return { startIdx, endIdx };
};

const loadLatestYearIfNeeded = async (symbolInfo) => {
  if (window.barCache["1"].length > 0) return;

  showTVLoader();
  const latestYear = Math.max(...AVAILABLE_YEARS);
  await loadYearIfNeeded(symbolInfo, latestYear);
  hideTVLoader();
};

// ===============================
// RANDOM START (OPTIONAL)
// Call from UI: window.pickRandomCandle({name:"XAU-USD_REPLAY"}) OR pass symbolInfo
// ===============================
window.pickRandomCandle = async (symbolInfo) => {
  const y = AVAILABLE_YEARS[Math.floor(Math.random() * AVAILABLE_YEARS.length)];
  await loadYearIfNeeded(symbolInfo, y);

  const bars = window.barCache["1"];
  if (!bars.length) return null;

  const idx = Math.floor(Math.random() * bars.length);
  window.replayIndex = idx;
  window.replayEnd = bars[idx].time;
  window.replayMode = true;
  window._lastReplayPrice = bars[idx].close;

  console.log(`🎲 Random replay start: year=${y}, idx=${idx}, time=${new Date(window.replayEnd).toISOString()}`);
  return { idx, time: window.replayEnd };
};
// ===============================
// TRUE AUTO MULTI-YEAR LOADER (RECOMMENDED)
// Uses existing year system (no Android AUTO hacks)
// ===============================
window.startAutoReplay = async (symbolInfo) => {
  try {
    console.log("🎲 AUTO REPLAY START");

    const baseSymbol = symbolInfo.name.replace("_REPLAY", "");
    window._lastSymbol = baseSymbol; // 🔥 REQUIRED for forward year loading

    // ===============================
    // 🔥 SMART YEAR SELECTION: Only pick from years user has downloaded
    // ===============================
    const downloadedYears = await getDownloadedYearsForSymbol(baseSymbol);

    if (!downloadedYears || downloadedYears.length === 0) {
      console.warn("⚠️ No downloaded year files found for:", baseSymbol);
      return { error: "no_data", message: "No data downloaded yet. Please download at least one year of data first." };
    }

    // Pick random from ONLY the downloaded years
    const randomYear = downloadedYears[Math.floor(Math.random() * downloadedYears.length)];

    console.log("📅 Downloaded years:", downloadedYears, "→ AUTO selected year:", randomYear);

    // ===============================
    // 🔥 SMART CACHE STRATEGY (PRO)
    // ===============================

    const alreadyLoadedYears = Array.from(window.loadedYears);

    // If chart currently has 2025 and auto picks 2025 → DO NOTHING (fast)
    if (
      alreadyLoadedYears.length === 1 &&
      alreadyLoadedYears[0] === randomYear &&
      window.barCache["1"].length > 0
    ) {
      console.log("⚡ Using already loaded year:", randomYear);
    } else {
      // Different year selected → wipe old cache (CRITICAL)
      console.log("🧹 Wiping old cache. Old years:", alreadyLoadedYears);
      console.log("📦 Loading fresh year:", randomYear);

      window.barCache["1"] = [];
      window.loadedYears.clear();
      window.replayIndex = -1;
      window.replayEnd = null;
      window._lastReplayPrice = null;

      // Load ONLY selected year
      await loadYearIfNeeded({ name: baseSymbol }, randomYear);
    }

    const bars = window.barCache["1"];

    if (!bars || bars.length < 500) {
      console.error("❌ Not enough bars after AUTO load:", bars?.length);
      return null;
    }

    // Safe random index (avoid edges)
    const safeStart = 300;
    const safeEnd = bars.length - 300;

    const idx =
      Math.floor(Math.random() * (safeEnd - safeStart)) + safeStart;

    const selectedBar = bars[idx];

    window.replayIndex = idx;
    window.replayEnd = selectedBar.time;
    window.replayMode = true;
    window._lastReplayPrice = selectedBar.close;

    // 📰 CRITICAL FIX: Sync news index with replay start (MILLISECONDS SAFE)
    if (window.newsEvents && window.newsEvents.length > 0) {
      try {
        let lo = 0;
        let hi = window.newsEvents.length - 1;
        let ans = 0;

        while (lo <= hi) {
          const mid = (lo + hi) >> 1;
          if (window.newsEvents[mid].time <= window.replayEnd) {
            ans = mid + 1;
            lo = mid + 1;
          } else {
            hi = mid - 1;
          }
        }

        // 🔥 IMPORTANT: reset pointer to correct position
        if (window.newsIndexRef) {
          window.newsIndexRef.current = ans;
        }

        console.log("📰 News synced to replay start index:", ans);
      } catch (e) {
        console.warn("News sync failed:", e);
      }
    }
    console.log("🚀 AUTO READY:");
    console.log("Year =", randomYear);
    console.log("Index =", idx);
    console.log("Time =", new Date(selectedBar.time));
    console.log("TotalBars =", bars.length);
    console.log("LoadedYears =", Array.from(window.loadedYears));

    return { idx, time: selectedBar.time, year: randomYear };

  } catch (err) {
    console.error("❌ AUTO REPLAY FAILED:", err);
    return null;
  }
};

// ===============================
// TRUE RESTORE SESSION LOADER
// ===============================
window.restoreReplaySession = async (symbolInfo, sessionTime) => {
  try {
    console.log("💾 RESTORE REPLAY START");

    const baseSymbol = symbolInfo.name.replace("_REPLAY", "");
    window._lastSymbol = baseSymbol; 

    // Find the year containing the exact replay time
    const targetYear = new Date(sessionTime).getUTCFullYear();
    console.log("📅 Restoring session for year:", targetYear);

    // Completely wipe barcache (prevents time violation errors)
    window.resetBarCacheForRestore();

    // Load the target year AND previous year (prevents TradingView lookback crashes)
    await loadYearIfNeeded({ name: baseSymbol }, targetYear);
    await loadYearIfNeeded({ name: baseSymbol }, targetYear - 1);

    const CACHE = window.barCache["1"];
    if (!CACHE || CACHE.length < 100) {
      console.error("❌ Not enough bars after RESTORE load");
      return null;
    }

    // ⭐ Find index by exact timestamp instead of relying on a saved array index
    //    which may have shifted due to older bars being inserted
    const exactIdx = findIndexByTime(sessionTime);
    
    if (exactIdx === -1) {
      console.error("❌ Could not find exact replay time in cache:", new Date(sessionTime));
      return null;
    }

    window.replayIndex = exactIdx;
    window.replayEnd = sessionTime;
    window.replayMode = true;
    window._lastReplayPrice = CACHE[exactIdx].close;

    // Fix news offset for restored session
    if (window.newsEvents && window.newsEvents.length > 0) {
      try {
        let lo = 0; let hi = window.newsEvents.length - 1; let ans = 0;
        while (lo <= hi) {
          const mid = (lo + hi) >> 1;
          if (window.newsEvents[mid].time <= sessionTime) {
            ans = mid + 1; lo = mid + 1;
          } else {
            hi = mid - 1;
          }
        }
        if (window.newsIndexRef) window.newsIndexRef.current = ans;
      } catch (e) {
        console.warn("News sync failed on restore:", e);
      }
    }

    console.log("🚀 RESTORE READY:");
    console.log("Target Year =", targetYear);
    console.log("Matched Index =", exactIdx);
    console.log("Time =", new Date(sessionTime));

    return { idx: exactIdx, time: sessionTime, year: targetYear };

  } catch (err) {
    console.error("❌ RESTORE REPLAY FAILED:", err);
    return null;
  }
};

// ===============================
// CORE DATAFEED METHODS
// ===============================
// CORE DATAFEED METHODS (REPLAY + LAZY YEAR + 1D SAFE)
// ===============================
const getBars = async (symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) => {
  try {
    _currentResolution = resolution;
    // ==========================================================
    // 🔥 TF CHANGE RESET (SMART UX LIKE TRADINGVIEW)
    // MUST be before loading years and before aggregation
    // ==========================================================
    if (window._lastTfUsed !== resolution) {
      console.log(
        `🔄 TF Changed: ${window._lastTfUsed} → ${resolution}. Resetting deep history cache.`
      );

      // Only reset if we already have data (avoid first load wipe)
      if (window.barCache["1"] && window.barCache["1"].length > 0 && window.loadedYears.size > 0) {
        const latestYear = Math.max(...window.loadedYears);

        const bars = window.barCache["1"];

        // Keep ONLY latest year (lightweight reset)
        const pruned = bars.filter(bar => {
          const y = new Date(bar.time).getUTCFullYear();
          return y === latestYear;
        });

        window.barCache["1"] = pruned;
        window.loadedYears = new Set([latestYear]);

        console.log(
          "🧹 TF Reset Applied:",
          "keptYear =", latestYear,
          "bars =", pruned.length
        );
      }

      window._deepHistoryLoaded = false;
      window._lastTfUsed = resolution;
    }

    const baseSymbol = symbolInfo.name.replace("_REPLAY", "");
    // 🔥 LOAD NEWS (ONLY ONCE PER SYMBOL)
    // 📰 ENSURE NEWS IS FULLY LOADED BEFORE REPLAY START
    if (!window._newsLoaded || window._newsSymbol !== baseSymbol) {
      await window.loadNewsBINForSymbol(baseSymbol);
    }

    // 🔥 Only clear cache when REAL symbol changes (not replay switch)
    if (window._lastSymbol !== baseSymbol) {
      console.log("🔄 Base symbol changed, clearing cache");
      window._lastSymbol = baseSymbol;

      window.barCache["1"] = [];
      window.loadedYears.clear();
      window.replayIndex = -1;
      window.replayEnd = null;
      window._lastReplayPrice = null;
    }

    // Always ensure at least latest year is loaded
    // Always ensure at least latest year is loaded
    await loadLatestYearIfNeeded({ name: baseSymbol });

    const oneMinBars = window.barCache["1"];
    if (!oneMinBars.length) {
      console.warn("⚠️ No 1m bars available after load");
      onHistoryCallback([], { noData: true });
      return;
    }

    // ==========================================================
    // 🌍 UNIVERSAL TF-SAFE CHRONOLOGICAL YEAR LOADER (ADD HERE)
    // Works for ALL TF: 1m, 5m, 15m, 1H, 4H, 1D
    // ==========================================================
    // ==========================================================
    // 🌍 STRICT CHRONOLOGICAL YEAR LOADER (NO YEAR SKIP - FINAL FIX)
    // ==========================================================
    if (window.loadingYear === null && periodParams?.from) {
      const fromMs = periodParams.from * 1000;
      const requestedYear = new Date(fromMs).getUTCFullYear();

      const oldestLoadedYear =
        window.loadedYears.size > 0
          ? Math.min(...window.loadedYears)
          : new Date(oneMinBars[0].time).getUTCFullYear();

      const prevYear = oldestLoadedYear - 1;

      console.log(
        "📚 History check:",
        "requestedYear=", requestedYear,
        "oldestLoadedYear=", oldestLoadedYear,
        "prevYear=", prevYear,
        "loadedYears=", Array.from(window.loadedYears)
      );

      // 🚫 STOP if user is requesting older than immediate previous year
      // Example: loaded=2022, requested=2018 → DO NOT jump years
      if (requestedYear < prevYear) {
        console.log(
          "🛑 Missing intermediate year. Strict stop.",
          "Need year:", prevYear,
          "But requested:", requestedYear
        );
        // DO NOTHING (very important)
      }
      // ✅ Load ONLY the immediate previous year (STRICT)
      else if (
        requestedYear < oldestLoadedYear &&
        AVAILABLE_YEARS.includes(prevYear) &&
        !window.loadedYears.has(prevYear) &&
        !window.missingYears.has(prevYear) // 🔥 ADD THIS LINE
      ) {
        console.log(
          `📦 Strict chronological load: ${oldestLoadedYear} → ${prevYear}`
        );

        await loadYearIfNeeded({ name: baseSymbol }, prevYear);

        console.log(
          "✅ Loaded years after strict load:",
          Array.from(window.loadedYears)
        );
      }
      // 🛑 If previous year file does NOT exist → STOP COMPLETELY
      else if (!AVAILABLE_YEARS.includes(prevYear)) {
        console.log(
          "🛑 Previous year file missing. STOP loading.",
          "missingYear=", prevYear
        );
        // DO NOT search 2020/2019/2018 anymore
      }
    }

    const isReplay = symbolInfo.name.includes("_REPLAY");
    const from = periodParams.from * 1000;
    const to = periodParams.to * 1000;
    const countBack = periodParams.countBack || 300;

    // ==========================================================
    // 🎬 REPLAY MODE (FIXED: 1D + SCROLL + HISTORICAL LAZY LOAD)
    // ==========================================================
    if (isReplay) {
      // 🚨 CRITICAL: Block unstable dataset during year loading (prevents infinite loading)
      if (window.loadingYear !== null) {
        console.log(
          `⏳ REPLAY getBars blocked while loading year: ${window.loadingYear}`
        );

        const stableAgg = aggregateReplayFrom1m(
          resolution,
          Math.max(0, window.replayIndex)
        );

        if (stableAgg.length > 0) {
          onHistoryCallback(stableAgg, { noData: false });
        } else {
          onHistoryCallback([], { noData: true });
        }

        window._isReadyForRealtime = true;
        return;
      }

      // 🔥 CHRONOLOGICAL HISTORICAL LOADER (FINAL FIX - NO YEAR SKIP)
      const oldestLoadedTime = oneMinBars[0].time;

      // 🚀 USE TRUE DATASET YEARS (NOT CACHE TIME!)
      const oldestLoadedYear =
        window.loadedYears.size > 0
          ? Math.min(...window.loadedYears)
          : new Date(oldestLoadedTime).getUTCFullYear();

      const requestedYear = new Date(from).getUTCFullYear();
      const absoluteOldestYear = Math.min(...AVAILABLE_YEARS);

      if (requestedYear < absoluteOldestYear) {
        console.log(
          "🛑 Absolute oldest year reached. Stopping history search.",
          "requestedYear=", requestedYear,
          "absoluteOldestYear=", absoluteOldestYear
        );

        const sliced = aggregateRangeFrom1m(
          resolution,
          from,
          Math.min(to, window.replayEnd ?? to),
          window.replayIndex,
          countBack
        );

        onHistoryCallback(sliced, { noData: true }); // 🔥 CRITICAL
        window._isReadyForRealtime = true;
        return;
      }
      else if (from < oldestLoadedTime) {
        console.log(
          "📜 REPLAY needs older history.",
          "from=", new Date(from).toISOString(),
          "oldestLoadedYear(real)=", oldestLoadedYear,
          "loadedYears=", Array.from(window.loadedYears)
        );

        // 🚨 LOAD STRICTLY PREVIOUS YEAR ONLY (NO JUMPS EVER)
        const prevYear = oldestLoadedYear - 1;

        if (
          AVAILABLE_YEARS.includes(prevYear) &&
          !window.loadedYears.has(prevYear) &&
          window.loadingYear !== prevYear
        ) {
          console.log(
            `📦 Chronological load: ${oldestLoadedYear} → ${prevYear}`
          );

          await loadYearIfNeeded(symbolInfo, prevYear);
          window._deepHistoryLoaded = true; // 🔥 mark deep scroll session

          console.log(
            "✅ Years after load:",
            Array.from(window.loadedYears)
          );
        } else {
          console.log(
            "ℹ️ Previous year already loaded or unavailable:",
            prevYear
          );
        }
      }




      // Refresh bars after possible lazy load
      const replayOneMin = window.barCache["1"];

      if (!replayOneMin.length) {
        onHistoryCallback([], { noData: true });
        return;
      }

      // 🔥 STEP 2: Ensure replay index is valid
      if (window.replayIndex < 0) {
        window.replayIndex = Math.min(200, replayOneMin.length - 1);
        window.replayEnd = replayOneMin[window.replayIndex].time;
        window._lastReplayPrice = replayOneMin[window.replayIndex].close;
      } else if (window.replayEnd != null) {
        const idx = findIndexByTime(window.replayEnd);
        if (idx >= 0) window.replayIndex = idx;
      }

      // ✅ STEP 3: LIGHTWEIGHT REPLAY RANGE AGGREGATION (NO HUGE 1D CACHE)
      const replayTo = Math.min(to, window.replayEnd ?? to);

      const sliced = aggregateRangeFrom1m(
        resolution,
        from,
        replayTo,
        window.replayIndex, // uptoIndex (replay)
        countBack
      );

      console.log(
        `🎬 REPLAY getBars | TF=${resolution} | replayIdx=${window.replayIndex} | returned=${sliced.length} | loadedYears=${Array.from(window.loadedYears)}`
      );

      // 🔥 Store last bar for subscribeBars (TV requirement)
      if (sliced.length > 0) {
        lastBarsCache.set(symbolInfo.name, { ...sliced[sliced.length - 1] });
      }

      onHistoryCallback(sliced, { noData: sliced.length === 0 });

      // ✅ Self-subscribe fallback:
      // TV sometimes calls getBars(new TF) but never follows with subscribeBars –
      // seen as 10-second hangs after a fast TF switch.
      //
      // Callback priority (best → fallback):
      //   1. _lastOnRealtimeCallback  – set by subscribeBars for the new TF (fast path)
      //   2. _preSwitchCallback       – saved from the OLD subscription before TF switch
      //      (TV reuses the same internal callback infrastructure; safe across TFs)
      //
      // GUARD: only activate if this getBars is for the CURRENT desired resolution.
      const desiredRes = window._desiredResolution || window._lastTfUsed || resolution;
      const isCorrectTF = resolution === desiredRes;
      const fallbackCb = window._lastOnRealtimeCallback || window._preSwitchCallback || null;
      const fallbackUID = window._lastSubscriberUID || window._preSwitchUID || ("auto_" + resolution);

      if (!window._isReadyForRealtime && fallbackCb && isCorrectTF) {
        const src = window._lastOnRealtimeCallback ? "subscribeBars" : "preSwitchBackup";
        console.log(`[getBars] Self-activating pump for TF=${resolution} via ${src}`);

        window._onRealtimeCallback = fallbackCb;
        window._activeSubscriber = {
          uid: fallbackUID,
          symbol: symbolInfo.name,
          resolution,
        };
        // Push the current candle so TV has a live bar immediately
        if (sliced.length > 0) {
          try { window._onRealtimeCallback({ ...sliced[sliced.length - 1] }); } catch (_) { }
        }
        // Consume the pre-switch backup so it can't pollute a future switch
        window._preSwitchCallback = null;
        window._preSwitchUID = null;
        window._isReadyForRealtime = true;
      }

      return;
    }

    // ==========================================================
    // 📊 NORMAL MODE (RANGE + LAZY YEAR LOAD)
    // ==========================================================
    // ==========================================================
    // 📊 NORMAL MODE (RANGE + SMART YEAR STOP FIX)
    // ==========================================================
    const oldestLoadedTime = oneMinBars[0].time;
    const oldestLoadedYear =
      window.loadedYears.size > 0
        ? Math.min(...window.loadedYears)
        : new Date(oldestLoadedTime).getUTCFullYear();

    const absoluteOldestYear = Math.min(...AVAILABLE_YEARS);
    const requestedYear = new Date(from).getUTCFullYear();

    // 🛑 CRITICAL FIX: If user requests older than available dataset → STOP TV LOADING
    if (requestedYear < absoluteOldestYear) {
      console.log(
        "🛑 Reached absolute oldest dataset.",
        "requestedYear=", requestedYear,
        "absoluteOldestYear=", absoluteOldestYear
      );

      const filtered = aggregateRangeFrom1m(
        resolution,
        from,
        to,
        null,
        countBack
      );

      // 🔥 Tell TradingView: NO MORE HISTORY EXISTS
      onHistoryCallback(filtered, { noData: true });
      return;
    }

    // 🔥 Only load previous year if file actually exists
    if (from < oldestLoadedTime) {
      const prevYear = oldestLoadedYear - 1;

      if (!AVAILABLE_YEARS.includes(prevYear)) {
        console.log(
          "🛑 Previous year file NOT available:",
          prevYear,
          "LoadedYears=", Array.from(window.loadedYears)
        );

        const filtered = aggregateRangeFrom1m(
          resolution,
          from,
          to,
          null,
          countBack
        );

        // 🔥 THIS LINE FIXES YOUR 2020 → 2019 INFINITE LOADING BUG
        onHistoryCallback(filtered, { noData: true });
        return;
      }

      if (
        AVAILABLE_YEARS.includes(prevYear) &&
        !window.loadedYears.has(prevYear) &&
        window.loadingYear !== prevYear
      ) {
        console.log(`📥 NORMAL: Need older data. Loading prevYear=${prevYear}`);
        await loadYearIfNeeded({ name: baseSymbol }, prevYear);
        window._deepHistoryLoaded = true;
      }
    }

    // ✅ LIGHTWEIGHT NORMAL RANGE AGGREGATION (NO FULL DATASET BUILD)
    const filtered = aggregateRangeFrom1m(
      resolution,
      from,
      to,
      null,       // not replay
      countBack
    );

    console.log(
      `📊 NORMAL getBars | TF=${resolution} | returned=${filtered.length} | loadedYears=${Array.from(window.loadedYears)}`
    );

    if (!filtered.length) {
      onHistoryCallback([], { noData: true });
    } else {
      onHistoryCallback(filtered, { noData: false });
    }

  } catch (err) {
    console.error("❌ [GET_BARS ERROR]", err);
    onErrorCallback(err?.message || "Datafeed Error");
  }
};
// ===============================
// MEMORY PRUNING (KEEP DATA LIGHT)
// ===============================
const MAX_ACTIVE_YEARS = 999;  // 🔥 Keep only 2 years in RAM (recommended)

const pruneOldYearsIfNeeded = () => {
  if (window.loadedYears.size <= MAX_ACTIVE_YEARS) return;

  const years = Array.from(window.loadedYears).sort((a, b) => b - a); // newest first
  const keepYears = new Set(years.slice(0, MAX_ACTIVE_YEARS));

  console.log("🧹 Pruning old years. Keeping:", Array.from(keepYears));

  const bars = window.barCache["1"];
  if (!bars.length) return;

  const pruned = bars.filter(bar => {
    const y = new Date(bar.time).getUTCFullYear();
    return keepYears.has(y);
  });

  window.barCache["1"] = pruned;
  window.loadedYears = keepYears;

  console.log("🧹 After prune: bars =", pruned.length, "years =", Array.from(keepYears));
};

// ===============================
// SUBSCRIBE / UNSUBSCRIBE
// ===============================
let _subscriberToken = 0;

const subscribeBars = (symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback) => {
  const myToken = ++_subscriberToken;

  // 🔥 Always save the latest callback as a fallback for the self-subscribe
  // path in getBars (covers the case where TV delays calling subscribeBars).
  window._lastOnRealtimeCallback = onRealtimeCallback;
  window._lastSubscriberUID = subscriberUID;

  // 🔥 Snapshot the global TF-switch sequence at the moment this subscribe fired.
  const myTfSeq = window._tfSwitchSeq;

  // Always kill the OLD callback first so the pump can't emit stale data.
  window._onRealtimeCallback = null;
  window._isReadyForRealtime = false;
  window._onResetCacheNeeded = onResetCacheNeededCallback || null;

  _currentResolution = resolution;

  window._activeSubscriber = {
    uid: subscriberUID,
    symbol: symbolInfo.name,
    resolution,
  };

  // 🔥 RACE GUARD: if another subscribe already ran after us, bail.
  if (myToken !== _subscriberToken) {
    console.warn(`[SUBSCRIBE] Stale token (${myToken} vs ${_subscriberToken}), skipping`);
    return;
  }

  // 🔥 If a newer TF switch happened, bail — TV will call us again.
  if (myTfSeq !== window._tfSwitchSeq) {
    console.warn(`[SUBSCRIBE] Stale TF seq (${myTfSeq} vs ${window._tfSwitchSeq}), skipping`);
    return;
  }

  // If the self-subscribe fallback in getBars already activated the pump
  // for this exact TF, just update the callback reference (fresh TV callback)
  // and return — no need to emit again.
  if (window._isReadyForRealtime && window._activeSubscriber?.resolution === resolution) {
    window._onRealtimeCallback = onRealtimeCallback;
    console.log(`✅ [SUBSCRIBE] Refreshed callback. TF=${resolution} UID=${subscriberUID}`);
    return;
  }

  window._onRealtimeCallback = onRealtimeCallback;
  window._lastEmittedCandleTime = 0;

  // Push current candle immediately (LIGHTWEIGHT - NO FULL AGG)
  const oneMinBars = window.barCache["1"];

  if (!oneMinBars || oneMinBars.length === 0) {
    console.warn("[SUBSCRIBE] No bars yet");
    window._isReadyForRealtime = true;
    return;
  }

  let idx;
  if (window.replayMode) {
    idx = window.replayIndex >= 0
      ? window.replayIndex
      : Math.min(200, oneMinBars.length - 1);
  } else {
    idx = oneMinBars.length - 1;
  }

  const endTime = window.replayMode
    ? (window.replayEnd ?? oneMinBars[idx]?.time)
    : oneMinBars[oneMinBars.length - 1]?.time;

  const startTime = endTime - (400 * getResolutionMs(resolution));

  const agg = aggregateRangeFrom1m(
    resolution,
    startTime,
    endTime,
    window.replayMode ? idx : null,
    300
  );

  const last = agg[agg.length - 1];
  if (last) emitRealtime(last);

  // ✅ Single gate: only subscribeBars (or the self-subscribe fallback) opens the pump.
  window._isReadyForRealtime = true;

  console.log(`✅ [SUBSCRIBE] TF=${resolution} UID=${subscriberUID} seq=${myTfSeq}`);
};

const unsubscribeBars = (subscriberUID) => {
  if (!window._activeSubscriber) return;
  if (subscriberUID !== window._activeSubscriber.uid) return;

  // 🔥 PRESERVE callback into _preSwitchCallback BEFORE clearing.
  // After TV's ~10s unsubscribe timeout, the poll's forced setResolution() retry
  // triggers onIntervalChanged again, which will look for _preSwitchCallback.
  // By saving it here, our self-subscribe fallback in getBars still has a
  // valid callback even after standard paths have cleared everything.
  if (window._onRealtimeCallback || window._lastOnRealtimeCallback) {
    window._preSwitchCallback = window._onRealtimeCallback || window._lastOnRealtimeCallback;
    window._preSwitchUID = window._lastSubscriberUID || subscriberUID;
    console.log("[UNSUBSCRIBE] Preserved _preSwitchCallback for retry fallback");
  }

  window._onRealtimeCallback = null;
  window._isReadyForRealtime = false;
  window._activeSubscriber = null;

  console.log(`✅ [UNSUBSCRIBE] UID=${subscriberUID}`);

  // 🔥 CRITICAL: Tell TV to reset bar cache for new TF (same as demo datafeed)
  // Without this, TV delays calling subscribeBars by up to 10 seconds
  if (typeof window._onResetCacheNeeded === "function") {
    try {
      window._onResetCacheNeeded();
      console.log("🔄 [UNSUBSCRIBE] Cache reset signaled to TV");
    } catch (e) { }
    window._onResetCacheNeeded = null;
  }
};

// ===============================
// REPLAY PUMP (ADVANCE 1m ONLY)
// ===============================
window.pumpNextBar = () => {
  if (
    window._isPumpBusy ||
    !window.replayMode
  ) return null;

  window._isPumpBusy = true;

  try {
    const bars = window.barCache["1"];
    if (!bars || bars.length === 0) return null;

    const maxIndex = bars.length - 1;

    // Safety init
    if (window.replayIndex < 0) {
      window.replayIndex = Math.min(200, maxIndex);
      window._lastEmittedCandleTime = 0;
    }

    // ===============================
    // 🚀 FORWARD YEAR AUTO LOADER (FINAL FIX)
    // ===============================
    if (window.replayIndex >= maxIndex) {
      const bars = window.barCache["1"];
      if (!bars || bars.length === 0) return null;

      const lastBar = bars[maxIndex];
      const lastYear = new Date(lastBar.time).getUTCFullYear();
      const nextYear = lastYear + 1;

      console.log(
        "📅 Replay reached end of year:",
        lastYear,
        "→ Trying to load next year:",
        nextYear
      );

      // 🔥 Only load if file exists & not already loaded
      if (
        AVAILABLE_YEARS.includes(nextYear) &&
        !window.loadedYears.has(nextYear) &&
        !window.missingYears.has(nextYear) &&
        window.loadingYear === null
      ) {
        console.log(`📦 Forward loading next year: ${nextYear}`);

        // IMPORTANT: async load but DO NOT advance index yet
        loadYearIfNeeded(
          { name: window._lastSymbol || "XAU-USD" },
          nextYear
        );

        return null; // wait for new data
      }

      console.log("🛑 No next year available. Replay finished.");
      return null;
    }
    // 🔥 ALWAYS advance replay (EVEN WITHOUT SUBSCRIBER)
    window.replayIndex += 1;

    const currentBar = bars[window.replayIndex];
    if (!currentBar) return null;

    window.replayEnd = currentBar.time;
    window._lastReplayPrice = currentBar.close;

    // Determine resolution safely
    const res =
      window._desiredResolution ||
      window._activeSubscriber?.resolution ||
      window._lastTfUsed ||
      "1";

    const candle = buildCurrentReplayCandle(res, window.replayIndex);
    if (!candle) return null;
    // Note: renderNewsUpToTime is called (throttled, every 5 bars) from executeNextStep
    // in advanced_chart.js. Do NOT call it here too — double-calling on every bar
    // causes expensive chart shape creation and replay lag.

    // 🔥 ONLY emit if subscriber exists (no freeze!)
    if (window._onRealtimeCallback && window._isReadyForRealtime) {
      window._onRealtimeCallback({
        time: candle.time,
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
        volume: candle.volume,
      });

      window._lastEmittedCandleTime = candle.time;
    }

    return candle;

  } finally {
    window._isPumpBusy = false;
  }
};

// ===============================
// RESET
// ===============================
window.resetDatafeedForReplay = () => {
  window._isReadyForRealtime = false;
  window._onRealtimeCallback = null;
  window._activeSubscriber = { uid: null, symbol: null, resolution: null };

  _currentResolution = null;
  _isPumpBusy = false;

  window.replayIndex = -1;
  window.replayEnd = null;
  window._lastReplayPrice = null;

  console.log("[DATAFEED] Replay Reset (1m cache kept)");
};
const resetReplay = () => {
  window.replayMode = false;
  window.replayEnd = null;
  window.replayIndex = -1;
  window._lastReplayPrice = null;

  window._isReadyForRealtime = false;
  window._onRealtimeCallback = null;
  window._activeSubscriber = { uid: null, symbol: null, resolution: null };

  console.log("🔄 Replay fully reset");
};
// ===============================
// REPLAY STATUS (FOR UI LOOP)
// ===============================
window.getReplayStatus = () => {
  const hasCb = typeof window._onRealtimeCallback === "function";
  const hasSub = !!window._activeSubscriber?.uid;

  // "ready" means TradingView subscribed + callback attached
  const ready = window.replayMode && hasCb && window._isReadyForRealtime && hasSub;

  return {
    mode: !!window.replayMode,
    busy: !!window._isPumpBusy,          // optional
    wait: window.replayMode && !ready,   // waiting for subscribeBars
    ready,
    replayIndex: window.replayIndex,
    loadedBars: window.barCache?.["1"]?.length || 0,
    subscriber: window._activeSubscriber || null,
  };
};

// ===============================
// EXPORTS
// ===============================
export { getBars, subscribeBars, unsubscribeBars, resetReplay };

export default {
  getBars,
  subscribeBars,
  unsubscribeBars,
  resetReplay,

  // 📰 ADD THIS BLOCK HERE
  getMarks: (symbolInfo, from, to, onDataCallback) => {
    console.log("📰 getMarks called", from, to);

    if (!window.newsEvents || window.newsEvents.length === 0) {
      onDataCallback([]);
      return;
    }

    const marks = window.newsEvents
      .filter(e => e.time >= from * 1000 && e.time <= to * 1000)
      .map(e => ({
        id: e.time,
        time: Math.floor(e.time / 1000),

        color:
          e.impact === 3 ? "#FF5252" :   // 🔴 high
            e.impact === 2 ? "#FFC107" :   // 🟡 medium
              "#2196F3",                     // 🔵 low

        text: e.title || "News",
        label: "N",
        labelFontColor: "#fff",
        minSize: 14
      }));

    console.log("📰 Marks sent:", marks.length);
    onDataCallback(marks);
  },

  configuration: {
    supported_resolutions: ['1', '2', '3', '5', '10', '15', '30', '60', '120', '180', '240', '1D']
  },
};